import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";
import { CURRENCIES, currencySymbol } from "@/lib/constants";
import CustomerLocationsSection from "@/components/CustomerLocationsSection";

const PRESETS = [25000, 50000, 100000, 150000, 200000, 500000];
const TAX_PRESETS = [
  { label: "GST 5% (AB, BC, MB, NT, NU, YT, SK)", v: 5 },
  { label: "HST 13% (ON)", v: 13 },
  { label: "HST 15% (NB, NL, NS, PE)", v: 15 },
  { label: "QC 14.975%", v: 14.975 },
  { label: "None", v: 0 },
];

const emptyForm = {
  email: "",
  name: "",
  company: "",
  phone: "",
  billing_contact_name: "",
  billing_contact_phone: "",
  billing_email: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "Canada",
  notes: "",
  credit_limit: "",
  credit_currency: "USD",
  credit_notes: "",
  tax_rate: "",
  is_active: true,
  send_email: true,
};

export default function CustomerEditDialog({ open, onOpenChange, customer, onSaved }) {
  // Create mode when no customer is passed in
  const isEdit = !!customer?.id;
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState(null); // for post-create step

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        email: customer.email || "",
        name: customer.name || "",
        company: customer.company || "",
        phone: customer.phone || "",
        billing_contact_name: customer.billing_contact_name || "",
        billing_contact_phone: customer.billing_contact_phone || "",
        billing_email: customer.billing_email || "",
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        zip: customer.zip || "",
        country: customer.country || "Canada",
        notes: customer.notes || "",
        credit_limit: customer.credit_limit ?? "",
        credit_currency: customer.credit_currency || "USD",
        credit_notes: customer.credit_notes || "",
        tax_rate: customer.tax_rate ?? "",
        is_active: customer.is_active !== false,
        send_email: false,
      });
    } else {
      setForm(emptyForm);
    }
    setCredentials(null);
  }, [open, customer, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!isEdit && !form.email.trim()) { toast.error("Email is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        credit_limit: form.credit_limit === "" ? 0 : Number(form.credit_limit),
        tax_rate: form.tax_rate === "" ? 0 : Number(form.tax_rate),
        billing_email: (form.billing_email || "").trim() || null,
      };
      if (isEdit) {
        // strip create-only fields
        delete payload.email;
        delete payload.send_email;
        await api.patch(`/customers/${customer.id}`, payload);
        toast.success("Customer updated");
        onSaved?.();
        onOpenChange(false);
      } else {
        const { data } = await api.post("/customers", payload);
        setCredentials({
          email: data.customer.email,
          name: data.customer.name,
          password: data.temp_password,
          email_status: data.email_status,
          email_error: data.email_error,
        });
        onSaved?.();
        // Dialog stays open showing credentials — user closes manually
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const outstanding = customer?.outstanding_total || 0;
  const sym = currencySymbol(form.credit_currency);

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sharp max-w-2xl max-h-[90vh] overflow-y-auto border-zinc-950">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight">
            {isEdit ? "Edit customer" : "New customer"}
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-600">
            {isEdit ? (
              <>Portal login: <span className="font-mono">{customer?.email}</span></>
            ) : (
              "Create a customer with contact + billing details. An email with login credentials will be sent."
            )}
          </DialogDescription>
        </DialogHeader>

        {credentials ? (
          <div className="space-y-3" data-testid="customer-cred-panel">
            <div className={`p-3 text-white ${
              credentials.email_status === "sent" ? "bg-emerald-600" :
              credentials.email_status === "failed" ? "bg-rose-600" : "bg-amber-500"
            }`}>
              <div className="flex items-center gap-2 font-semibold tracking-wide text-xs uppercase">
                <Mail className="h-4 w-4" />
                {credentials.email_status === "sent" ? "CREDENTIALS EMAILED"
                 : credentials.email_status === "failed" ? "EMAIL FAILED"
                 : "EMAIL PENDING"}
              </div>
              <p className="text-xs mt-1 opacity-90">
                {credentials.email_status === "sent"
                  ? `Login details have been sent to ${credentials.email}.`
                  : credentials.email_status === "failed"
                    ? `Delivery failed${credentials.email_error ? ` (${credentials.email_error})` : ""}. Copy the password below.`
                    : "SMTP is not configured yet. Copy the password below and share it manually."}
              </p>
            </div>
            <div className="border border-zinc-950 divide-y divide-zinc-200 text-sm">
              <div className="flex items-stretch">
                <div className="w-32 bg-zinc-50 px-3 py-2 mono-label border-r border-zinc-200 flex items-center">Email</div>
                <div className="flex-1 px-3 py-2 flex items-center justify-between font-mono">
                  <span>{credentials.email}</span>
                  <button onClick={() => copy(credentials.email)} className="ml-2 p-1 border border-zinc-300 hover:bg-zinc-100"><Copy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="flex items-stretch">
                <div className="w-32 bg-zinc-50 px-3 py-2 mono-label border-r border-zinc-200 flex items-center">Password</div>
                <div className="flex-1 px-3 py-2 flex items-center justify-between font-mono font-semibold text-[#0056B3]">
                  <span data-testid="cust-cred-password">{credentials.password}</span>
                  <button onClick={() => copy(credentials.password)} className="ml-2 p-1 border border-zinc-300 hover:bg-zinc-100"><Copy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-zinc-600 font-mono">Ask the customer to change their password on first login.</p>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="cust-cred-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          {/* ————— Portal login ————— */}
          {!isEdit && (
            <div className="border border-zinc-200 bg-zinc-50 p-3">
              <div className="mono-label mb-2">CUSTOMER PORTAL LOGIN</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="mono-label">Email *</label>
                  <Input type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="sharp mt-1 apex-input bg-white" data-testid="cust-email"
                    placeholder="ap@customer.com" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-2">
                This is the customer&apos;s portal sign-in email. Invoices will be sent to this address unless a separate billing email is provided below.
              </p>
            </div>
          )}

          <div>
            <div className="mono-label mb-2">CONTACT DETAILS</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono-label">Full name *</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sharp mt-1 apex-input" data-testid="cust-name" />
              </div>
              <div>
                <label className="mono-label">Company</label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="sharp mt-1 apex-input" data-testid="cust-company" />
              </div>
              <div>
                <label className="mono-label">Phone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="sharp mt-1 apex-input" />
              </div>
              <div>
                <label className="mono-label">Billing email (for invoices)</label>
                <Input type="email" value={form.billing_email}
                  onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                  className="sharp mt-1 apex-input"
                  placeholder={isEdit ? customer.email : "defaults to portal email"}
                  data-testid="cust-billing-email" />
              </div>
              <div>
                <label className="mono-label">Billing contact name</label>
                <Input value={form.billing_contact_name} onChange={(e) => setForm({ ...form, billing_contact_name: e.target.value })} className="sharp mt-1 apex-input" />
              </div>
              <div>
                <label className="mono-label">Billing contact phone</label>
                <Input value={form.billing_contact_phone} onChange={(e) => setForm({ ...form, billing_contact_phone: e.target.value })} className="sharp mt-1 apex-input" />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-2">BILLING ADDRESS</div>
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-6">
                <label className="mono-label">Street address</label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sharp mt-1 apex-input" />
              </div>
              <div className="col-span-3">
                <label className="mono-label">City</label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="sharp mt-1 apex-input" />
              </div>
              <div className="col-span-1">
                <label className="mono-label">State / Prov</label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="sharp mt-1 apex-input font-mono uppercase" maxLength={3} />
              </div>
              <div className="col-span-1">
                <label className="mono-label">Zip / Postal</label>
                <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="sharp mt-1 apex-input font-mono" />
              </div>
              <div className="col-span-1">
                <label className="mono-label">Country</label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="sharp mt-1 apex-input" />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-3">CREDIT LIMIT</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="mono-label">Amount (0 = unlimited)</label>
                <Input type="number" step="1" min="0" value={form.credit_limit}
                  onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                  className="sharp mt-1 apex-input font-mono" data-testid="cust-credit-limit" />
                <div className="flex flex-wrap gap-1 mt-2">
                  {PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => setForm({ ...form, credit_limit: p })}
                      className="px-2 py-1 border border-zinc-300 text-[10px] font-mono hover:bg-zinc-100">
                      {sym}{(p / 1000).toFixed(0)}K
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mono-label">Currency</label>
                <Select value={form.credit_currency} onValueChange={(v) => setForm({ ...form, credit_currency: v })}>
                  <SelectTrigger className="sharp mt-1" data-testid="cust-credit-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isEdit && (
              <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
                <div className="mono-label mb-1">CURRENT UTILIZATION</div>
                <div className="text-sm font-mono">
                  {sym}{outstanding.toLocaleString()} outstanding
                  {form.credit_limit && Number(form.credit_limit) > 0 && (
                    <> · {((outstanding / Number(form.credit_limit)) * 100).toFixed(1)}% of {sym}{Number(form.credit_limit).toLocaleString()}</>
                  )}
                </div>
                {form.credit_limit && Number(form.credit_limit) > 0 && (
                  <div className="mt-2 h-2 bg-zinc-200 relative">
                    <div
                      className={`absolute inset-y-0 left-0 ${
                        outstanding >= Number(form.credit_limit) ? "bg-rose-600" :
                        outstanding / Number(form.credit_limit) > 0.8 ? "bg-amber-500" : "bg-emerald-600"
                      }`}
                      style={{ width: `${Math.min(100, (outstanding / Number(form.credit_limit)) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            <div className="mt-3">
              <label className="mono-label">Credit notes (internal)</label>
              <Textarea rows={2} value={form.credit_notes} onChange={(e) => setForm({ ...form, credit_notes: e.target.value })} className="sharp mt-1 apex-input" />
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <div className="mono-label mb-3">SALES TAX (GST/HST/PST)</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mono-label">Tax rate (%)</label>
                <Input type="number" step="0.01" min="0" value={form.tax_rate}
                  onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                  className="sharp mt-1 apex-input font-mono" placeholder="0" data-testid="cust-tax-rate" />
              </div>
              <div className="col-span-2 flex flex-wrap gap-1 items-end pb-1">
                {TAX_PRESETS.map((p) => (
                  <button key={p.label} type="button"
                    onClick={() => setForm({ ...form, tax_rate: p.v })}
                    className="px-2 py-1 border border-zinc-300 text-[10px] font-mono hover:bg-zinc-100"
                    data-testid={`tax-preset-${p.v}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-2 text-[11px] text-zinc-600">
              Applied automatically when generating invoices for this customer.
            </p>
          </div>

          <div className="border-t border-zinc-200 pt-4">
            <label className="mono-label">Internal notes</label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sharp mt-1 apex-input" />
          </div>

          {isEdit && <CustomerLocationsSection customerId={customer.id} />}

          {isEdit ? (
            <div className="flex items-center gap-2 border-t border-zinc-200 pt-4">
              <Checkbox id="cust-active" checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} data-testid="cust-active" />
              <label htmlFor="cust-active" className="text-sm cursor-pointer">
                Account active (uncheck to block all new job requests)
              </label>
            </div>
          ) : (
            <div className="flex items-center gap-2 border-t border-zinc-200 pt-4">
              <Checkbox id="cust-send-email" checked={form.send_email}
                onCheckedChange={(v) => setForm({ ...form, send_email: !!v })} data-testid="cust-send-email" />
              <label htmlFor="cust-send-email" className="text-sm cursor-pointer">
                Email login credentials to the new customer
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="sharp border-zinc-300" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="sharp bg-[#0056B3] hover:bg-[#004494]" data-testid="cust-submit">
              {busy ? "Saving..." : isEdit ? "Save changes" : "Create customer"}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
